import React from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2 } from "lucide-react";
import { CustomerDetails } from "@/components/customers/CustomerDetails";
import { Customer } from "../types";
import DetailPageLayout from "@/components/layout/DetailPageLayout";
import DetailPageHeader from "@/components/layout/DetailPageHeader";

interface CustomerDetailsViewProps {
  selectedCustomer: Customer | null;
  setViewMode: () => void;
  setEditing: (customer: Customer | null) => void;
  setOpen: (open: boolean) => void;
  handleDeleteCustomer: (customerId: string) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  navigateToCustomer: (customerId: string) => void;
}

export const CustomerDetailsView: React.FC<CustomerDetailsViewProps> = ({
  selectedCustomer,
  setViewMode,
  setEditing,
  setOpen,
  handleDeleteCustomer,
  activeTab,
  setActiveTab,
  navigateToCustomer
}) => {
  const navigate = useNavigate();

  if (!selectedCustomer) {
    return (
      <DetailPageLayout>
        <div className="text-center py-8">
          <p className="text-lg text-muted-foreground">No customer selected</p>
        </div>
      </DetailPageLayout>
    );
  }

  const customerName = selectedCustomer.customer_type === 'commercial' && selectedCustomer.business_name
    ? selectedCustomer.business_name
    : `${selectedCustomer.name} ${selectedCustomer.last_name}`;

  return (
    <DetailPageLayout>
      <DetailPageHeader
        title={customerName}
        backLink={{ label: "Back to Customers", onClick: setViewMode }}
        primaryAction={{
          label: "Edit",
          icon: Edit,
          onClick: () => { setEditing(selectedCustomer); setOpen(true); }
        }}
        secondaryActions={[
          {
            label: "Delete",
            icon: Trash2,
            onClick: () => handleDeleteCustomer(selectedCustomer.id),
            destructive: true,
            requiresConfirmation: {
              title: "Delete Customer",
              description: `Are you sure you want to delete "${customerName}" and all associated data? This action cannot be undone.`
            }
          }
        ]}
      />
      
      <CustomerDetails
        selectedCustomer={selectedCustomer}
        setViewMode={setViewMode}
        setEditing={setEditing}
        setOpen={setOpen}
        handleDeleteCustomer={handleDeleteCustomer}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        navigateToCustomer={navigateToCustomer}
      />
    </DetailPageLayout>
  );
}
